import { differenceInHours, differenceInMinutes } from 'date-fns';
import { millisecondsInHour, millisecondsInMinute, millisecondsInSecond } from 'date-fns/constants';
import { useState } from 'react';
import { match, P } from 'ts-pattern';
import { useInterval } from 'usehooks-ts';
import { formatDateRelative } from '../../lib/formatters';

// Determine how often the relative date should be re-calculated (every second,
// every minute or every hour)
const getUpdateInterval = <T extends Date | number | string | undefined>(
  timestamp: T
): number | null => {
  if (!timestamp) return null;
  const now = new Date();
  const target = new Date(timestamp);
  const minutesDiff = Math.abs(differenceInMinutes(target, now));
  const hoursDiff = Math.abs(differenceInHours(target, now));

  return match([minutesDiff, hoursDiff])
    .with([P.when((m) => m < 1), P._], () => millisecondsInSecond)
    .with([P._, P.when((h) => h < 1)], () => millisecondsInMinute)
    .otherwise(() => millisecondsInHour);
};

// Display a relative date, with an interval to ensure that smaller relative
// times are updated frequently.
export const RelativeDate = <T extends Date | number | string | undefined>({
  timestamp,
  formatter = formatDateRelative,
}: {
  timestamp: T;
  formatter?: (timestamp: T) => string;
}) => {
  const [_now, setNow] = useState(new Date());

  useInterval(() => {
    setNow(new Date());
  }, getUpdateInterval(timestamp));

  return formatter(timestamp);
};
